import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y'
import { FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay'
import { Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core'
import { MatMenuPanel, MatMenuTrigger } from '@angular/material/menu'
import { Subject, takeUntil } from 'rxjs'

/**
 * Yksi valikon käynnistin, joka avaa valikon klikatun elementin kohdalle. Jokainen oma käynnistin lisäisi
 * elementtiin neljä kuuntelijaa, mikä tuhansilla kirjausriveillä on liikaa, joten rivit käsittelevät klikkauksen
 * komponentin yhteisessä käsittelijässä ja avaavat valikon tällä.
 */
@Directive({
  selector: '[appJaettuValikko]',
  exportAs: 'appJaettuValikko',
  standalone: false,
})
export class JaettuValikkoDirective extends MatMenuTrigger implements OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _kohdistus = inject(FocusMonitor)
  private _kohde: HTMLElement | null = null
  private _irtoamisenTarkkailu: MutationObserver | null = null

  @Input('appJaettuValikko')
  get valikko(): MatMenuPanel {
    return this.menu
  }
  set valikko(valikko: MatMenuPanel) {
    this.menu = valikko
  }

  constructor() {
    super()
    this.menuClosed.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this._irtoamisenTarkkailu?.disconnect()
      this._kohde?.setAttribute('aria-expanded', 'false')
    })
  }

  override ngOnDestroy() {
    this._irtoamisenTarkkailu?.disconnect()
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
    super.ngOnDestroy()
  }

  /** Avaa valikon kohteen kohdalle kuten kohteen oma käynnistin: hiirellä avattu valikko ei näytä kohdistusta ensimmäisessä kohdassa. */
  avaa(kohde: HTMLElement, data: unknown, tapahtuma: MouseEvent) {
    if (this.menuOpen) {
      return
    }
    // Overlay luodaan kerran ensimmäisestä kohteesta, joten myöhemmät avaukset siirtävät sen uuteen kohteeseen
    const sijainti = this._overlayRef?.getConfig().positionStrategy as FlexibleConnectedPositionStrategy | undefined
    sijainti?.setOrigin(kohde)
    // Edellisen sulkemisen animaation aikana valikon sisältö on yhä kiinnitetty edellisen kohteen tiedoilla
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach()
      this.menu?.lazyContent?.detach()
    }
    this._kohde = kohde
    this.menuData = data
    this._openedBy = tapahtuma.detail === 0 ? 'keyboard' : 'mouse'
    kohde.setAttribute('aria-expanded', 'true')
    this.openMenu()
    this._suljeKunKohdeIrtoaa(kohde)
  }

  /**
   * Kohteen oma käynnistin sulki valikon tuhoutuessaan kohteen mukana, esimerkiksi kun taustalla päivittynyt kirjaus
   * korvaa rivit. Muuten avoin valikko muokkaisi korvattua riviä, ja muutos katoaisi.
   */
  private _suljeKunKohdeIrtoaa(kohde: HTMLElement) {
    this._irtoamisenTarkkailu?.disconnect()
    this._irtoamisenTarkkailu = new MutationObserver(() => {
      if (!kohde.isConnected) {
        this.closeMenu()
      }
    })
    this._irtoamisenTarkkailu.observe(this._element.nativeElement.parentElement ?? document.body, { childList: true, subtree: true })
  }

  protected override _getOverlayOrigin(): ElementRef<HTMLElement> {
    return this._kohde ? new ElementRef(this._kohde) : super._getOverlayOrigin()
  }

  override focus(origin?: FocusOrigin, options?: FocusOptions) {
    const kohde = this._kohde?.isConnected ? this._kohde : this._element.nativeElement
    if (origin) {
      this._kohdistus.focusVia(kohde, origin, options)
    } else {
      kohde.focus(options)
    }
  }
}
